import { AudioManager } from './AudioManager';
import { MESSAGES, getMessageText, MessageType } from '../config/messages';

export class MockAIService {
    private audioManager: AudioManager;

    constructor(audioManager: AudioManager) {
        this.audioManager = audioManager;
    }
    private responses = {
        wordSuggestions: ['חתול', 'כלב', 'פרח', 'דג', 'ציפור', 'כוכב', 'שמש', 'ירח', 'בית', 'מים'],
        encouragement: [
            "מצוין! זה נהדר!",
            "כל הכבוד! אתם אלופים!",
            "יפה מאוד! בואו נמשיך!",
            "מעולה! קדימה לאות הבאה!"
        ],
        celebration: [
            "אדיר! הקלדתם את המילה בהצלחה! אתם אלופים!",
            "מעולה! הקלדתם את המילה בהצלחה! כל הכבוד!",
            "מושלם! המילה הוקلדה נהדר!"
        ]
    };

    async chatWithChild(userMessage: string, gameContext: any, voiceService?: any): Promise<string> {
        await new Promise(resolve => setTimeout(resolve, 200)); // Reduced from 500ms

        const { childName, stage, isSimpleMode } = gameContext;

        if (isSimpleMode) {
            if (stage === 'ask_word') {
                return "איזו מילה הייתם רוצים להקליד כעת?";
            }
        } else {
            // New streamlined flow without gender
            if (stage === 'greeting_and_ask_name') {
                return MESSAGES.GREETING_AND_ASK_NAME;
            }

            if (stage === 'ask_name') {
                return MESSAGES.ASK_NAME;
            }

            if (stage === 'lets_start') {
                return MESSAGES.LETS_START;
            }

            if (stage === 'ask_word') {
                return MESSAGES.ASK_WORD;
            }

            // Legacy support for old stages (backward compatibility)
            if (stage === 'ask_gender') {
                return MESSAGES.ASK_GENDER;
            }

            if (stage === 'ask_ready') {
                return MESSAGES.GREETING_WITH_NAME(childName || 'חבר');
            }
        }

        if (stage === 'celebration') {
            const response = this.responses.celebration[Math.floor(Math.random() * this.responses.celebration.length)];
            return response;
        }

        return "איזה כיף לכתוב מילים ביחד";
    }

    extractWordFromResponse(userMessage: string): string {
        console.log(`🔍 Extracting word from: "${userMessage}"`);
        
        if (!userMessage || userMessage.trim().length === 0) {
            throw new Error('Cannot extract word from empty input');
        }
        
        const words = this.responses.wordSuggestions;

        // Check if user said one of our suggested words
        for (const word of words) {
            if (userMessage.includes(word)) {
                console.log(`✅ Found suggested word: "${word}"`);
                return word;
            }
        }

        // Extract Hebrew words from input (allow up to 15 characters for long words)
        const hebrewWords = userMessage.match(/[\u0590-\u05FF]{2,15}/g);
        if (hebrewWords && hebrewWords.length > 0) {
            console.log(`✅ Extracted Hebrew word: "${hebrewWords[0]}"`);
            return hebrewWords[0];
        }

        // Fallback to random word
        console.log(`⚠️ No word found, using random fallback`);
        return words[Math.floor(Math.random() * words.length)];
    }

    getTypingGuidance(letter: string, wrongKey?: string): string {
        const letterPositions: { [key: string]: string } = {
            'ח': 'ליד האות "ל" ו-"י"',
            'ז': 'בתחתית המקלדת, ליד האות "ס"',
            'י': 'במרכז המקלדת, ליד האות "ח"',
            'ר': 'בשורה העליונה, ליד האות "ק"',
            'ש': 'בצד שמאל של המקלדת',
            'ל': 'ליד האות "ך" ו-"ח"',
            'ו': 'בשורה העליונה, ליד האות "ט"',
            'ם': 'בשורה העליונה, ליד האות "פ"'
        };

        const position = letterPositions[letter] || 'במקלדת';
        const intro = wrongKey ? "כמעט! " : "";
        return `${intro}חפשו את האות "${letter}" במקלדת. היא נמצאת ${position}.`;
    }

    getEncouragement(): string {
        return this.responses.encouragement[Math.floor(Math.random() * this.responses.encouragement.length)];
    }

    // Audio-enhanced methods that prioritize audio over TTS
    async playMessage(messageType: 'ask_word' | 'greeting_and_ask_name' | 'lets_start' | 'ask_gender' | 'ask_name', voiceService: any): Promise<boolean> {
        try {
            console.log(`🎵 Attempting to play message: ${messageType}`);
            const audioPlayed = await this.audioManager.playMessage(messageType);
            
            if (!audioPlayed) {
                console.log(`📢 Audio not available for ${messageType}, using TTS fallback`);
                const text = this.getMessageText(messageType);
                await voiceService.speak(text);
                return false; // Indicate TTS was used
            }
            
            console.log(`✅ Audio played successfully for ${messageType}`);
            return true; // Indicate audio was used
        } catch (error) {
            console.error(`Failed to play message ${messageType}:`, error);
            const text = this.getMessageText(messageType);
            await voiceService.speak(text);
            return false;
        }
    }

    async playEncouragement(voiceService: any): Promise<boolean> {
        try {
            console.log('🎵 Attempting to play encouragement');
            const audioPlayed = await this.audioManager.playEncouragement();
            
            if (!audioPlayed) {
                console.log('📢 Audio not available for encouragement, using TTS fallback');
                const text = this.getEncouragement();
                await voiceService.speak(text);
                return false;
            }
            
            console.log('✅ Encouragement audio played successfully');
            return true;
        } catch (error) {
            console.error('Failed to play encouragement:', error);
            const text = this.getEncouragement();
            await voiceService.speak(text);
            return false;
        }
    }

    async playCelebration(voiceService: any): Promise<boolean> {
        try {
            console.log('🎵 Attempting to play celebration');
            const audioPlayed = await this.audioManager.playCelebration();
            
            if (!audioPlayed) {
                console.log('📢 Audio not available for celebration, using TTS fallback');
                const text = this.responses.celebration[Math.floor(Math.random() * this.responses.celebration.length)];
                await voiceService.speak(text);
                return false;
            }
            
            console.log('✅ Celebration audio played successfully');
            return true;
        } catch (error) {
            console.error('Failed to play celebration:', error);
            const text = this.responses.celebration[Math.floor(Math.random() * this.responses.celebration.length)];
            await voiceService.speak(text);
            return false;
        }
    }

    async playGreeting(childName: string, voiceService: any): Promise<boolean> {
        try {
            console.log('🎵 Attempting to play greeting');
            await this.audioManager.playGreeting(childName, voiceService);
            return true;
        } catch (error) {
            console.error('Failed to play greeting:', error);
            await voiceService.speak(MESSAGES.GREETING_WITH_NAME(childName));
            return false;
        }
    }

    // New method for lets_start stage
    async playLetsStart(childName: string, voiceService: any): Promise<boolean> {
        try {
            console.log('🎵 Attempting to play lets_start message');
            const audioPlayed = await this.audioManager.playMessage('lets_start');
            
            if (!audioPlayed) {
                console.log('📢 Audio not available for lets_start, using TTS fallback');
                const text = MESSAGES.LETS_START;
                await voiceService.speak(text);
                return false;
            }
            
            console.log('✅ Lets start audio played successfully');
            return true;
        } catch (error) {
            console.error('Failed to play lets_start:', error);
            const text = MESSAGES.LETS_START;
            await voiceService.speak(text);
            return false;
        }
    }

    async playWordIntroduction(word: string, voiceService: any): Promise<boolean> {
        try {
            console.log('🎵 Attempting to play word introduction');
            await this.audioManager.playWordIntroduction(word, voiceService);
            return true;
        } catch (error) {
            console.error('Failed to play word introduction:', error);
            const fullMessage = MESSAGES.WORD_INTRODUCTION(word);
            await voiceService.speak(fullMessage);
            return false;
        }
    }

    async playLetterGuidance(letter: string, voiceService: any, wrongKey?: string): Promise<boolean> {
        try {
            console.log(`🎵 Attempting to play letter guidance for ${letter}${wrongKey ? ' (wrong key: ' + wrongKey + ')' : ''}`);
            await this.audioManager.playLetterGuidanceWithPrefix(letter, wrongKey, voiceService);
            return true;
        } catch (error) {
            console.error(`Failed to play letter guidance for ${letter}:`, error);
            const text = this.getTypingGuidance(letter, wrongKey);
            await voiceService.speak(text);
            return false;
        }
    }

    // Enhanced method for playing typing guidance with proper audio/TTS handling
    async playTypingGuidance(letter: string, voiceService: any, wrongKey?: string): Promise<void> {
        await this.playLetterGuidance(letter, voiceService, wrongKey);
    }

    // Method to stop all currently playing audio
    stopAllAudio(): void {
        this.audioManager.stopAllAudio();
        console.log('🛑 All audio stopped via AIService');
    }

    private getMessageText(messageType: string): string {
        return getMessageText(messageType as MessageType) || "";
    }
}